import { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AuthLayout } from '../components/Layout/AuthLayout';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import './Auth.css';



export function Register() {
  usePageTitle('Criar conta');
  const { register } = useAuth();
  // Veio de um link de convite com restrição de domínio (ex: /register?domain=escritorio.com.br)
  const [searchParams] = useSearchParams();
  const inviteDomain = searchParams.get('domain')?.trim().toLowerCase() || null;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (inviteDomain && formData.email.trim().split('@').pop()?.toLowerCase() !== inviteDomain) {
      setError(`Para usar este convite, cadastre-se com um e-mail @${inviteDomain}.`);
      return;
    }
    setLoading(true);
    try {
      await register({
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        password: formData.password,
        org_name: `Org ${formData.firstName}`
      });
    } catch (err: any) {
      setError(err.message || 'Falha ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout currentStep={1}>
      <div className="auth-page">
        <div className="auth-header">
          <h2 className="auth-title">Criar Conta</h2>
          <p className="auth-subtitle">Insira seus dados pessoais para criar sua conta.</p>
        </div>

        {inviteDomain && (
          <div style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            color: 'var(--color-primary-400)',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            textAlign: 'center'
          }}>
            Você está entrando por um convite. Use seu e-mail <strong>@{inviteDomain}</strong> — vamos confirmá-lo por e-mail antes de liberar o acesso à organização.
            Já tem conta? <Link to="/login">Entre aqui</Link>.
          </div>
        )}


        {error && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            color: '#f87171',
            padding: 'var(--space-3)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-row">
            <Input 
              label="Nome" 
              name="firstName" 
              placeholder="ex. João" 
              value={formData.firstName}
              onChange={handleChange}
              required 
              disabled={loading}
            />
            <Input 
              label="Sobrenome" 
              name="lastName" 
              placeholder="ex. Silva" 
              value={formData.lastName}
              onChange={handleChange}
              required 
              disabled={loading}
            />
          </div>

          <Input 
            label="E-mail" 
            name="email" 
            type="email" 
            placeholder={`ex. joao@${inviteDomain ?? 'empresa.com.br'}`}
            value={formData.email}
            onChange={handleChange}
            required 
            disabled={loading}
          />
          
          <Input 
            label="Senha" 
            name="password" 
            type="password" 
            placeholder="Crie uma senha forte" 
            value={formData.password}
            onChange={handleChange}
            required 
            disabled={loading}
          />
          <p className="auth-terms">A senha deve ter pelo menos 8 caracteres.</p>

          <Button type="submit" variant="secondary" fullWidth disabled={loading}>
            {loading ? 'Criando Conta...' : 'Criar Conta'}
          </Button>
        </form>

        <div className="auth-footer">
          Já tem uma conta? <Link to="/login">Entrar</Link>
        </div>
      </div>
    </AuthLayout>
  );
}
